import AOS from 'aos';
import 'dom-slider';
import { merge } from "lodash-es";
import LazyLoad from 'vanilla-lazyload';
import { createApp, defineAsyncComponent, ref } from 'vue';
import VueScrollTo from 'vue-scrollto';
import { i18n } from "./util";
import PhotoSwipe from './mixins/photo-swipe';
import VideoPlayer from './directives/video-player';
import PageHeader from "./components/PageHeader.vue";
const AjaxList = defineAsyncComponent(() => import('./components/AjaxList.vue'));
const Collapse = defineAsyncComponent(() => import('./components/Collapse.vue'));
const CustomScript = defineAsyncComponent(() => import('./components/CustomScript.vue'));
const Gallery = defineAsyncComponent(() => import('./components/Gallery.vue'));
const PageHeroSlider = defineAsyncComponent(() => import('./components/PageHeroSlider.vue'));
// TODO: SwiperSlide cannot be defined as async component yet, see: https://github.com/nolimits4web/swiper/issues/4613
// const SwiperSlide = defineAsyncComponent(() => import('swiper/esm/vue/swiper-slide').then(module => module.SwiperSlide));
import { SwiperSlide } from "swiper/vue";
const PREVENT_UNLOAD_CLASSES = [
    '.ajax',
    '.download',
    '#scroll-to-top',
    '[data-photoswipe]',
    '[download]',
    '[href^=\\#]',
    '[href*=ajax]',
    '[href^=javascript]',
    '[href^=mailto]',
    '[href^=tel]',
    '[href*=tx_typoscriptrendering]',
    '[target^=_blank]',
];
const SCROLL_OFFSET = 50;
const DESKTOP_BREAKPOINT = 768;
export const rootComponent = {
    /* == GLOBAL COMPONENTS == */
    components: {
        'ajax-list': AjaxList,
        collapse: Collapse,
        'custom-script': CustomScript,
        gallery: Gallery,
        'page-header': PageHeader,
        'page-hero-slider': PageHeroSlider,
        'swiper-slide': SwiperSlide
    },
    /* ======= OPTIONS ======= */
    delimiters: ['<%', '%>'],
    /* ======= DIRECTIVES ======= */
    directives: {
        'scroll-to': VueScrollTo,
        'video-player': VideoPlayer,
    },
    computed: {
        scrollOffset() {
            let offset = -SCROLL_OFFSET;
            if (this.header && this.header.value) {
                offset -= this.header.value.offsetHeight;
            }
            return offset;
        }
    },
    provide() {
        return {
            lazyLoad: this.lazyLoad,
            scrollOffset: this.scrollOffset
        };
    },
    /* ======== SETUP ======== */
    setup() {
        const header = ref(null);
        const lazyLoad = new LazyLoad({
            threshold: 0,
            elements_selector: '[lazy]',
            class_loading: 'lazy-loading',
            class_loaded: 'lazy-loaded',
            class_applied: 'lazy-bg-loaded',
            class_error: 'lazy-error',
        });
        const photoSwipe = new PhotoSwipe();
        return {
            header,
            lazyLoad,
            photoSwipe
        };
    },
    /* === LIFECYCLE HOOKS === */
    created() {
        window.addEventListener('load', this.onLoad);
        this.createdHook();
    },
    mounted() {
        AOS.init({
            duration: 900,
            once: true,
            disable: window.innerWidth < DESKTOP_BREAKPOINT,
        });
        this.lazyLoad.update();
        document.body.classList.add('loaded');
        this.mountedHook();
    },
    /* ======= METHODS ======= */
    methods: {
        /* === LIFECYCLE METHODS HOOKS === */
        createdHook() {
            /* Placeholder function used to extend Vue created hook in projects */
        },
        loadedHook() {
            /* Placeholder function used to extend document on-load event in projects */
        },
        mountedHook() {
            /* Placeholder function used to extend Vue mounted hook in projects */
        },
        /* ======= GENERAL METHODS ======= */
        initUnload() {
            let links = 'a';
            PREVENT_UNLOAD_CLASSES.forEach((className) => {
                links += `:not(${className})`;
            });
            document.querySelectorAll(links).forEach((link) => {
                link.addEventListener('click', (event) => {
                    const target = event.currentTarget;
                    if (event.ctrlKey || event.shiftKey || event.metaKey || event.button === 1) {
                        return true;
                    }
                    else if ((target === null || target === void 0 ? void 0 : target.pathname) === window.location.pathname) {
                        return true;
                    }
                    else if ((target === null || target === void 0 ? void 0 : target.getAttribute('id')) === 'history-back') {
                        event.preventDefault();
                        if (window.history.length > 1) {
                            window.history.back();
                        }
                        return false;
                    }
                    document.body.classList.remove('loaded');
                    return true;
                });
            });
        },
        onLoad() {
            document.body.classList.add('loaded');
            this.initUnload();
            this.loadedHook();
        },
    }
};
export * from "./plugins";
export default function (projectRootComponent) {
    const app = createApp(merge(rootComponent, projectRootComponent));
    app.use(i18n);
    return app;
}
// back button fixes
window.addEventListener('unload', () => { });
window.onpageshow = (event) => {
    if (event.persisted) {
        window.location.reload();
    }
};
